// ============================================================
// Missing beats - an attack the display did not play; missing walks - a move it did not walk
// ============================================================
// On a screen drawing the display, every attack in the log whose target has
// a marker there plays a beat (engine/display/beats, plan map-display D), and
// every move line whose figure has a marker there walks it (engine/display/walk,
// plan design-gaps M5). The page notes the beats and walks it started
// (diagnostics/beatLog); this compares. Not asked of: one from before the
// display was up, one too fresh to have started, one overtaken by a newer one
// on the same figure while it would still have been showing - a marker shows
// only the newest impact, a figure walks only its newest move - and one this
// screen heard only after it had expired (stale: a muted or offline screen
// catching up does not replay the fight).
//
// Pure: snapshots in, violations out.

import { IMPACT_TTL_MS } from '../impacts';
import { WALK_KEEP_MS } from '../display/walk';
import type { Violation } from './invariants';

/** An attack has started its beat by this long after it was logged, ms. */
export const BEAT_SETTLE_MS = 4000;
/** Only this recent a stretch of the log is looked at, ms; the page's record keeps a little longer (diagnostics/beatLog). */
export const BEAT_WINDOW_MS = 60_000;

/** What a page records of its beats (diagnostics/beatLog): since when the display is up (null: it is not), the beats played. */
export interface BeatLog {
  shownSince: number | null;
  /** The attacks this screen settled: played, or heard only after their impact had expired (owed nothing). */
  played: readonly string[];
  at: number;
}

export interface Attack {
  targetId: string;
  at: number;
}

/** A move line as the snapshot reads it (engine/moveBeat): who moved, when. */
interface LoggedMove {
  actorId: string;
  at: number;
}

interface BeatSnapshot {
  seat: { playerId?: string | null } | null;
  beats?: BeatLog | null;
  attacks?: readonly Attack[];
  moves?: readonly LoggedMove[];
  tokens?: unknown;
}

/** Something the log owes a display screen: whose marker it plays on, and when it was logged. */
interface Owed {
  who: string;
  at: number;
}

/** A newer attack on the same target while this one's impact still lived: a screen shows only the newest. */
export const overtaken = (a: Attack, all: readonly Attack[]) =>
  all.some((b) => b.targetId === a.targetId && b.at > a.at && b.at - a.at <= IMPACT_TTL_MS);

/** A newer one on the same figure within `ttl` of this one: the screen plays only the newer. */
const overtakenWithin = (o: Owed, all: readonly Owed[], ttl: number) =>
  all.some((b) => b.who === o.who && b.at > o.at && b.at - o.at <= ttl);

/** Within what this display should have played: since it came up, old enough to have started, inside the window. */
const inWindow = (beats: BeatLog, at: number) =>
  at >= beats.shownSince! && at <= beats.at - BEAT_SETTLE_MS && at >= beats.at - BEAT_WINDOW_MS;

/** What a display screen owed and did not settle: one window, one overtaking rule and one id shape for both. */
function missing(snap: BeatSnapshot, items: readonly Owed[], ttl: number, id: string, sentence: string): Violation[] {
  const beats = snap.beats;
  if (!beats || beats.shownSince === null) return [];
  const tokens = (snap.tokens ?? {}) as Record<string, unknown>;
  const played = new Set(beats.played);
  return items
    .filter((o) => inWindow(beats, o.at) && !!tokens[o.who] && !overtakenWithin(o, items, ttl))
    .filter((o) => !played.has(`${o.who}:${o.at}`))
    .map((o) => ({
      id,
      message: `${snap.seat?.playerId ?? 'a screen'}: ${sentence} ${o.who} at ${o.at} played nothing on the display`,
      data: o,
    }));
}

/** The attacks this display should have played and did not. */
export function missingBeats(snap: BeatSnapshot): Violation[] {
  const owed = (snap.attacks ?? []).map((a) => ({ who: a.targetId, at: a.at }));
  return missing(snap, owed, IMPACT_TTL_MS, 'missingBeat', 'the attack on');
}

/**
 * The moves this display should have walked and did not (plan design-gaps, M5). A newer move by the same figure
 * within what the shell keeps (useShellWalks `withFreshMoves`, WALK_KEEP_MS) excuses an older one delivered late.
 */
export function missingWalks(snap: BeatSnapshot): Violation[] {
  const owed = (snap.moves ?? []).map((m) => ({ who: m.actorId, at: m.at }));
  return missing(snap, owed, WALK_KEEP_MS, 'missingWalk', 'the move by');
}
